import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { setImpersonationToken } from "../lib/api";
import { BRAND_COLORS } from "../lib/bookingStatus";

/**
 * Weissblom · Impersonation-Bridge
 *
 * Feb 2026 · Konsumiert einen kurzlebigen Impersonation-JWT (aus dem QR-Code
 * im „Kundenansicht prüfen"-Modal des Teams) und tauscht ihn beim Backend
 * gegen einen echten `access_token`-Cookie ein. Danach Full-Page-Reload auf
 * `/app`, damit der AuthContext den frisch gesetzten Cookie erkennt und die
 * Kunden-Sicht rendert.
 *
 * Der JWT ist 2 min gültig; nach Consumption läuft eine reguläre Session
 * (30-Tage-Cookie) — für Live-Verifikation des Kunden-Home-Screens
 * ausreichend.
 */
export default function ImpersonateBridge() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = params.get("t");
    if (!t) { setError("Ungültiger Link — Parameter fehlt."); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/consume-impersonation", { token: t });
        if (cancelled) return;
        // Token nur in DIESEM Tab halten (sessionStorage) — kein Cookie, damit
        // die Admin-Session in anderen Tabs nicht überschrieben wird.
        setImpersonationToken(data?.token || null);
        window.location.href = "/app";
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || "Anmeldung fehlgeschlagen.");
      }
    })();
    return () => { cancelled = true; };
  }, [params, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ivory p-6" data-testid="impersonate-bridge">
      <div className="max-w-sm bg-white p-6 shadow-sm text-center">
        <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>Weissblom · Kundenansicht</div>
        {error ? (
          <>
            <div className="font-display text-lg text-navy mb-2">Link nicht mehr gültig</div>
            <div className="text-[13px] text-navy-dark/70 leading-snug" data-testid="impersonate-error">
              {error}
            </div>
            <div className="text-[11px] text-navy-dark/50 italic mt-4">
              Bitte im Team-Dashboard einen neuen QR-Code erzeugen (Gültigkeit 2 Minuten).
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center mb-3">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: BRAND_COLORS.navy }} strokeWidth={1.5} />
            </div>
            <div className="font-display text-lg text-navy mb-1">Anmeldung läuft …</div>
            <div className="text-[12.5px] text-navy-dark/70">
              Ihre Weissblom-Kundenansicht wird geladen.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
